/**
 * RoleContext.jsx - Role State Management
 * 
 * GRANULLO_MODE_LENS_UNIFICATION_v1.0 - PAUSE_GATE_1
 * 
 * Centralizes role state management into a proper React context.
 * This wraps the existing roleLensCopy.js utilities with:
 * - React Context for global state
 * - URL query param sync (?role=board|cfo|auditor|...)
 * - localStorage persistence
 * 
 * ROLE impacts COPY and CALLOUTS only - NOT permissions or features.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ROLE_LENS_OPTIONS,
  ROLE_LENS_COPY,
  DEFAULT_ROLE,
  ROLE_LENS_KEY,
  getCurrentRole,
  getRoleCopy,
  getRoleOption
} from '../lib/roleLensCopy';

// =============================================================================
// EXTENDED ROLE OPTIONS (12 roles per directive)
// =============================================================================

export const EXTENDED_ROLE_OPTIONS = [
  ...ROLE_LENS_OPTIONS,
  // Additional roles from directive
  {
    id: 'foundation',
    label: 'Foundation',
    shortLabel: 'Foundation',
    description: 'Non-profit program partner and fund recipient',
    icon: 'Heart',
    color: 'rose'
  },
  {
    id: 'donor',
    label: 'Donor',
    shortLabel: 'Donor',
    description: 'Contribution oversight and impact visibility',
    icon: 'Gift',
    color: 'pink'
  },
  {
    id: 'operator',
    label: 'Operator',
    shortLabel: 'Operator',
    description: 'Day-to-day execution and operations',
    icon: 'Settings',
    color: 'slate'
  },
  {
    id: 'beneficiary',
    label: 'Beneficiary',
    shortLabel: 'Beneficiary',
    description: 'End recipient of program funds',
    icon: 'User',
    color: 'sky'
  },
  {
    id: 'merchant',
    label: 'Merchant',
    shortLabel: 'Merchant',
    description: 'Authorized spend acceptance partner',
    icon: 'Store',
    color: 'orange'
  }
];

// Valid role IDs
export const VALID_ROLES = EXTENDED_ROLE_OPTIONS.map(r => r.id);

// =============================================================================
// CONTEXT
// =============================================================================

const RoleContext = createContext({
  role: DEFAULT_ROLE,
  setRole: () => {},
  roleOption: null,
  roleCopy: null,
  hasExplicitRole: false,
  resetRole: () => {},
  ROLE_OPTIONS: []
});

// =============================================================================
// PROVIDER
// =============================================================================

export const RoleProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize role from URL param, localStorage, or default
  const [role, setRoleState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_ROLE;
    
    // Priority: URL param > localStorage > default
    const urlRole = searchParams.get('role');
    if (urlRole && VALID_ROLES.includes(urlRole)) {
      return urlRole;
    }
    
    return getCurrentRole();
  });
  
  // Track if user has explicitly selected a role
  const [hasExplicitRole, setHasExplicitRole] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(ROLE_LENS_KEY) !== null;
  });
  
  // Set role with persistence and URL sync
  const setRole = useCallback((newRole) => {
    if (!VALID_ROLES.includes(newRole)) {
      console.warn(`Invalid role value: ${newRole}`);
      return;
    }
    
    setRoleState(newRole);
    setHasExplicitRole(true);
    
    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(ROLE_LENS_KEY, newRole);
    }
    
    // Update URL param (preserve other params)
    const newParams = new URLSearchParams(searchParams);
    newParams.set('role', newRole);
    setSearchParams(newParams, { replace: true });
    
    // Dispatch event for non-React listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('roleLensChanged', { detail: { role: newRole } }));
    }
  }, [searchParams, setSearchParams]);
  
  // Reset role to default
  const resetRole = useCallback(() => {
    if (typeof window === 'undefined') return;
    
    localStorage.removeItem(ROLE_LENS_KEY);
    setRoleState(DEFAULT_ROLE);
    setHasExplicitRole(false);
    
    // Remove from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('role');
    setSearchParams(newParams, { replace: true });
    
    // Dispatch event
    window.dispatchEvent(new CustomEvent('roleReset', {}));
  }, [searchParams, setSearchParams]);
  
  // Sync role from URL on navigation
  useEffect(() => {
    const urlRole = searchParams.get('role');
    if (urlRole && VALID_ROLES.includes(urlRole) && urlRole !== role) {
      setRoleState(urlRole);
      localStorage.setItem(ROLE_LENS_KEY, urlRole);
      setHasExplicitRole(true);
    }
  }, [searchParams, role]);
  
  // Get current role metadata
  const roleOption = EXTENDED_ROLE_OPTIONS.find(opt => opt.id === role) || 
                     getRoleOption(role) ||
                     EXTENDED_ROLE_OPTIONS.find(opt => opt.id === DEFAULT_ROLE);
  
  // Get role-specific copy
  const roleCopy = getRoleCopy(role);
  
  const value = {
    role,
    setRole,
    roleOption,
    roleCopy,
    hasExplicitRole,
    resetRole,
    ROLE_OPTIONS: EXTENDED_ROLE_OPTIONS,
    VALID_ROLES
  };
  
  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};

// =============================================================================
// HOOK
// =============================================================================

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within RoleProvider');
  }
  return context;
};

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Build URL with role param
 */
export const buildRoleUrl = (path, role) => {
  const url = new URL(path, window.location.origin);
  url.searchParams.set('role', role);
  return url.pathname + url.search;
};

/**
 * Build URL with both lens and role params
 */
export const buildLensRoleUrl = (path, lens, role) => {
  const url = new URL(path, window.location.origin);
  if (lens) url.searchParams.set('lens', lens);
  if (role) url.searchParams.set('role', role);
  return url.pathname + url.search;
};

export default RoleContext;
